import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Button, Table, Form, Modal,
  Spinner, Alert, Badge
} from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaTrash, FaArrowLeft, FaClipboardList, FaSave, FaTimes
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const TestName = () => {
  const navigate = useNavigate();
  const [testNames, setTestNames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [quizTypes, setQuizTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState({
    test_name: '',
    test_description: '',
    category_id: '',
    exam_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch dependencies and main data in parallel
      const [testsRes, catsRes, quizRes] = await Promise.all([
        apiService.get('/api/test-names'),
        apiService.get('/api/categories'),
        apiService.get('/api/quizTypes')
      ]);

      // Handle Test Names
      if (testsRes?.success && Array.isArray(testsRes.message?.testNames)) {
        setTestNames(testsRes.message.testNames);
      } else {
        // Fallback or empty if not found
        setTestNames(testsRes?.message?.data || []);
      }

      // Handle Categories
      if (catsRes?.success && Array.isArray(catsRes.message?.categories)) {
        setCategories(catsRes.message.categories);
      }

      // Handle Quiz Types (Exams)
      if (quizRes?.success && Array.isArray(quizRes.message?.quizTypes)) {
        setQuizTypes(quizRes.message.quizTypes);
      }

    } catch (err) {
      console.error(err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.test_name || !formData.category_id || !formData.exam_id) {
      setError('Please fill all required fields (Name, Category, Quiz Type).');
      return;
    }

    try {
      setError(null);
      const payload = {
        test_name: formData.test_name,
        test_description: formData.test_description,
        category_id: Number(formData.category_id),
        exam_id: Number(formData.exam_id)
      };

      let response;
      if (isEditMode && currentId) {
        response = await apiService.put(`/api/test-names/${currentId}`, payload);
      } else {
        response = await apiService.post('/api/test-names', payload);
      }

      if (response?.success) {
        setSuccessMsg(isEditMode ? 'Test updated successfully' : 'Test created successfully');
        setShowModal(false);
        fetchData();
      } else {
        setError(response?.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving data');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this test?')) return;
    
    try {
      const response = await apiService.delete(`/api/test-names/${id}`);
      if (response?.success) {
        setSuccessMsg('Test deleted successfully');
        fetchData();
      } else {
        setError(response?.message || 'Failed to delete test');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting test');
    }
  };

  const fetchExamDetails = async (examId) => {
    try {
      const response = await apiService.get(`/api/quizTypes/${examId}/details`);
      if (response?.success && response?.message?.quizDetails?.categories?.categoryList) {
        setAvailableCategories(response.message.quizDetails.categories.categoryList);
      } else {
        setAvailableCategories([]);
      }
    } catch (err) {
      console.error(err);
      setAvailableCategories([]);
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setIsEditMode(true);
      // Assuming primary key is test_name_id or id based on typical pattern
      setCurrentId(item.test_name_id || item.id);
      setFormData({
        test_name: item.test_name,
        test_description: item.test_description || '',
        category_id: item.category_id,
        exam_id: item.exam_id
      });
      if (item.exam_id) {
        fetchExamDetails(item.exam_id);
      }
    } else {
      setIsEditMode(false);
      setCurrentId(null);
      setFormData({
        test_name: '',
        test_description: '',
        category_id: '',
        exam_id: ''
      });
      setAvailableCategories([]);
    }
    setShowModal(true);
  };

  const getCategoryName = (id) => categories.find(c => c.category_id === id)?.category_name || id;
  const getQuizTypeName = (id) => quizTypes.find(q => q.quiz_type_id === id)?.quiz_type_name || id;

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button variant="outline-secondary" className="mb-2" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
          <h2 className="mb-0">
            <FaClipboardList className="me-2 text-primary" />
            Test Name Management
          </h2>
        </div>
        <Button variant="primary" onClick={() => openModal()}>
          <FaPlus className="me-2" /> Add New Test
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Table responsive hover className="align-middle">
              <thead className="bg-light">
                <tr>
                  <th>#</th>
                  <th>Test Name</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Quiz Type (Exam)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {testNames.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-4">No test names found.</td></tr>
                ) : (
                  testNames.map((item, index) => (
                    <tr key={item.test_name_id || item.id || index}>
                      <td>{index + 1}</td>
                      <td><strong>{item.test_name}</strong></td>
                      <td>{item.test_description || '-'}</td>
                      <td><Badge bg="info">{getCategoryName(item.category_id)}</Badge></td>
                      <td><Badge bg="secondary">{getQuizTypeName(item.exam_id)}</Badge></td>
                      <td>
                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => openModal(item)}>
                          <FaEdit />
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(item.test_name_id || item.id)}>
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Edit Test Name' : 'Create Test Name'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Test Name *</Form.Label>
                  <Form.Control
                    value={formData.test_name}
                    onChange={(e) => setFormData({ ...formData, test_name: e.target.value })}
                    placeholder="e.g. JLPT N5 Mock Test 1"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Quiz Type (Exam) *</Form.Label>
                  <Form.Select
                    value={formData.exam_id}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({ ...formData, exam_id: val, category_id: '' });
                      if (val) fetchExamDetails(val);
                      else setAvailableCategories([]);
                    }}
                  >
                    <option value="">Select Quiz Type</option>
                    {quizTypes.map(q => (
                      <option key={q.quiz_type_id} value={q.quiz_type_id}>
                        {q.quiz_type_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  >
                    <option value="">Select Category</option>
                    {availableCategories.map(c => (
                      <option key={c.category_id} value={c.category_id}>
                        {c.category_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    value={formData.test_description}
                    onChange={(e) => setFormData({ ...formData, test_description: e.target.value })}
                    placeholder="Optional description"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            <FaTimes className="me-2" /> Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            <FaSave className="me-2" />
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TestName;